/**
 * FHIR Questionnaire API facade.
 *
 * Delegates CRUD to FhirApiService and adds item-level operations (get/put/patch items, get/put/patch single item by
 * linkId).
 */

import { HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  CLIENT_ID_HEADER,
  IMPERSONATION_SESSION_HEADER,
  TIMEZONE_HEADER,
  VENDOR_ID_HEADER,
  skipInjectedHeaders,
} from '../../api/interceptors';
import type { FHIRItem, FHIRQuestionnaire } from '../models/fhir.model';
import { FhirApiService } from './fhir-api.service';
import type { FhirBundle, QuestionnaireSearchParams } from './fhir-api.types';
import { findItemByLinkId, mergeItemsByLinkId, patchItemByLinkId, replaceItemByLinkId } from './fhir-item-tree.utils';

const RESOURCE_TYPE = 'Questionnaire';

@Injectable()
export class FhirQuestionnaireApiService {
  private readonly fhirApi = inject(FhirApiService);

  getQuestionnaire(id: string): Observable<FHIRQuestionnaire> {
    return this.fhirApi.get<FHIRQuestionnaire>(RESOURCE_TYPE, id, this.fhirDbRequestOptions());
  }

  putQuestionnaire(questionnaire: FHIRQuestionnaire): Observable<FHIRQuestionnaire> {
    return this.fhirApi.put(questionnaire, this.fhirDbRequestOptions());
  }

  patchQuestionnaire(id: string, partial: Partial<FHIRQuestionnaire>): Observable<FHIRQuestionnaire> {
    return this.fhirApi.patch<FHIRQuestionnaire>(RESOURCE_TYPE, id, partial, this.fhirDbRequestOptions());
  }

  createQuestionnaire(questionnaire: FHIRQuestionnaire): Observable<FHIRQuestionnaire> {
    return this.fhirApi.create(questionnaire, this.fhirDbRequestOptions());
  }

  searchQuestionnaires(params?: QuestionnaireSearchParams): Observable<FhirBundle<FHIRQuestionnaire>> {
    return this.fhirApi.search<FHIRQuestionnaire>(RESOURCE_TYPE, params, this.fhirDbRequestOptions());
  }

  deleteQuestionnaire(id: string): Observable<void> {
    return this.fhirApi.delete(RESOURCE_TYPE, id, this.fhirDbRequestOptions());
  }

  // ---------- Item-level (top-level item array) ----------

  getQuestionnaireItems(questionnaireId: string): Observable<FHIRItem[]> {
    return this.getQuestionnaire(questionnaireId).pipe(map((q) => q.item ?? []));
  }

  putQuestionnaireItems(questionnaireId: string, items: FHIRItem[]): Observable<FHIRQuestionnaire> {
    return this.getQuestionnaire(questionnaireId).pipe(switchMap((q) => this.putQuestionnaire({ ...q, item: items })));
  }

  patchQuestionnaireItems(questionnaireId: string, items: FHIRItem[]): Observable<FHIRQuestionnaire> {
    return this.getQuestionnaire(questionnaireId).pipe(
      switchMap((q) => {
        const merged = mergeItemsByLinkId(q.item ?? [], items);
        return this.putQuestionnaire({ ...q, item: merged });
      })
    );
  }

  // ---------- Single item by linkId ----------

  getQuestionnaireItem(questionnaireId: string, linkId: string): Observable<FHIRItem | undefined> {
    return this.getQuestionnaireItems(questionnaireId).pipe(map((items) => findItemByLinkId(items, linkId)));
  }

  putQuestionnaireItem(questionnaireId: string, linkId: string, item: FHIRItem): Observable<FHIRQuestionnaire> {
    return this.getQuestionnaire(questionnaireId).pipe(
      switchMap((q) => {
        const root = q.item ?? [];
        const updated = replaceItemByLinkId(root, linkId, item);
        return this.putQuestionnaire({ ...q, item: updated });
      })
    );
  }

  patchQuestionnaireItem(
    questionnaireId: string,
    linkId: string,
    partial: Partial<FHIRItem>
  ): Observable<FHIRQuestionnaire> {
    return this.getQuestionnaire(questionnaireId).pipe(
      switchMap((q) => {
        const root = q.item ?? [];
        const updated = patchItemByLinkId(root, linkId, partial);
        return this.putQuestionnaire({ ...q, item: updated });
      })
    );
  }

  private fhirDbRequestOptions(): { context: HttpContext } {
    return {
      context: skipInjectedHeaders(new HttpContext(), [
        IMPERSONATION_SESSION_HEADER,
        CLIENT_ID_HEADER,
        VENDOR_ID_HEADER,
        TIMEZONE_HEADER,
      ]),
    };
  }
}
